import React, { useState, useEffect } from 'react';
import { X, FileText, ChevronDown, ChevronUp, Save, Upload, Trash2, FolderOpen } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { MultiSelect } from './ui/MultiSelect';
import { SearchableSelect } from './ui/SearchableSelect';
import { launchRequestApi, editorApi, authApi, verticalHeadsApi } from '../lib/api';
import { useAuth, isAdminRole } from '../contexts/AuthContext';
import { FILE_REQUEST_TYPES, OTHER_REQUEST_TYPE } from '../constants/fileRequestTypes';
import { PLATFORMS } from '../constants/platforms';
import { VERTICALS } from '../constants/verticals';
import { CanvasEditor } from './CanvasEditor';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

interface Editor {
  id: string;
  name: string;
  display_name?: string;
}

interface Buyer {
  id: string;
  name: string;
  email: string;
}

interface VerticalHead {
  head_editor_id: string;
  head_editor_name: string;
  head_editor_display_name?: string;
  head_editor_email?: string;
  vertical: string;
}

interface LRTemplate {
  id: string;
  name: string;
  default_request_type?: string;
  default_platforms?: string[];
  default_verticals?: string[];
  default_num_creatives?: number;
  default_suggested_run_qty?: number;
  default_concept_notes?: string;
  default_notes_to_creative?: string;
  default_notes_to_buyer?: string;
}

export function CreateLaunchRequestModal({ onClose, onSuccess }: Props) {
  const { user } = useAuth();
  const isEditorCreator = user?.role === 'creative' || user?.role === 'editor';
  // ── form state ────────────────────────────────────────────────────────────
  const [requestType, setRequestType] = useState('');
  const [customRequestType, setCustomRequestType] = useState('');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [verticals, setVerticals] = useState<string[]>([]);
  const [conceptNotes, setConceptNotes] = useState('');
  const [numCreatives, setNumCreatives] = useState('');
  const [suggestedRunQty, setSuggestedRunQty] = useState('');
  const [deliveryDeadline, setDeliveryDeadline] = useState('');
  const [testDeadline, setTestDeadline] = useState('');
  const [notesToCreative, setNotesToCreative] = useState('');
  const [notesToBuyer, setNotesToBuyer] = useState('');

  const [creativeHeadId, setCreativeHeadId] = useState('');
  const [buyerHeadId, setBuyerHeadId] = useState('');
  const [selectedEditorIds, setSelectedEditorIds] = useState<string[]>([]);
  const [selectedBuyerIds, setSelectedBuyerIds] = useState<string[]>([]);

  // ── template state ────────────────────────────────────────────────────────
  const [templates, setTemplates] = useState<LRTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // ── canvas ────────────────────────────────────────────────────────────────
  const [showCanvas, setShowCanvas] = useState(false);
  const [createdRequestId, setCreatedRequestId] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [canvasBriefFiles, setCanvasBriefFiles] = useState<File[]>([]);
  const [canvasBriefHeadline, setCanvasBriefHeadline] = useState('');
  const [canvasBriefScript, setCanvasBriefScript] = useState('');
  const [isBriefDragging, setIsBriefDragging] = useState(false);

  // ── drag-and-drop folder traversal ──────────────────────────────────────
  const traverseFileTree = async (item: any, files: File[]): Promise<void> => {
    if (item.isFile) {
      return new Promise((resolve) => {
        item.file((file: File) => { files.push(file); resolve(); });
      });
    } else if (item.isDirectory) {
      const dirReader = item.createReader();
      const readAllEntries = (): Promise<any[]> => {
        return new Promise((resolve) => {
          const allEntries: any[] = [];
          const readBatch = () => {
            dirReader.readEntries((entries: any[]) => {
              if (entries.length === 0) resolve(allEntries);
              else { allEntries.push(...entries); readBatch(); }
            });
          };
          readBatch();
        });
      };
      const entries = await readAllEntries();
      for (const entry of entries) {
        await traverseFileTree(entry, files);
      }
    }
  };

  const handleBriefDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsBriefDragging(false);
    const items = Array.from(e.dataTransfer.items);
    const files: File[] = [];
    const processItems = async () => {
      if (items.length > 0 && typeof items[0].webkitGetAsEntry === 'function') {
        for (const item of items) {
          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry();
            if (entry) await traverseFileTree(entry, files);
          }
        }
      } else {
        files.push(...Array.from(e.dataTransfer.files));
      }
      if (files.length > 0) setCanvasBriefFiles(prev => [...prev, ...files]);
    };
    processItems();
  };

  // ── data ──────────────────────────────────────────────────────────────────
  const [editors, setEditors] = useState<Editor[]>([]);
  const [buyers, setBuyers] = useState<Buyer[]>([]);           // for Assign Buyer Head dropdown
  const [verticalHeads, setVerticalHeads] = useState<Buyer[]>([]); // for Assign Vertical Head dropdown

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // ── fetch data ────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      // Load all in parallel — each with its own catch so one failure doesn't kill others
      const [editorsRes, buyersRes, vhRes, templatesRes] = await Promise.all([
        editorApi.getAll().catch((err: any) => { console.error('Failed to load editors:', err); return null; }),
        authApi.getBuyers().catch((err: any) => { console.error('Failed to load buyers:', err); return null; }),
        verticalHeadsApi.getAll().catch((err: any) => { console.error('Failed to load vertical heads:', err); return null; }),
        launchRequestApi.getTemplates().catch((err: any) => { console.error('Failed to load templates:', err); return null; }),
      ]);

      if (editorsRes) {
        const editorsData = editorsRes.data.data || [];
        setEditors(editorsData.map((e: any) => ({
          id: e.id,
          name: e.display_name || e.name,
          display_name: e.display_name
        })));
      }

      if (buyersRes) {
        const buyersData = buyersRes.data.data || buyersRes.data || [];
        setBuyers(buyersData);
      }

      // Build VH dropdown from vertical_heads data, with fallback to creative users
      let vhList: Buyer[] = [];
      if (vhRes) {
        const vhData: VerticalHead[] = vhRes.data.data || [];
        const uniqueVHs = new Map<string, Buyer>();
        vhData.forEach((vh) => {
          if (vh.head_editor_id && !uniqueVHs.has(vh.head_editor_id)) {
            uniqueVHs.set(vh.head_editor_id, {
              id: vh.head_editor_id,
              name: vh.head_editor_display_name || vh.head_editor_name || vh.vertical,
              email: vh.head_editor_email || '',
            });
          }
        });
        vhList = Array.from(uniqueVHs.values());
      }
      // Fallback: if no vertical heads found, load creative users directly
      if (vhList.length === 0) {
        try {
          const creativeRes = await authApi.getUsersByRole('creative');
          const creatives = creativeRes.data.data || creativeRes.data || [];
          vhList = creatives.map((u: any) => ({
            id: u.id,
            name: u.display_name || u.name,
            email: u.email || '',
          }));
        } catch (err) {
          console.error('Fallback: failed to load creative users:', err);
        }
      }
      setVerticalHeads(vhList);

      if (templatesRes) {
        setTemplates(templatesRes.data.data || []);
      }
    };
    load();
  }, []);

  // ── apply template ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedTemplateId) return;
    const tpl = templates.find(t => t.id === selectedTemplateId);
    if (!tpl) return;

    if (tpl.default_request_type) setRequestType(tpl.default_request_type);
    if (tpl.default_platforms?.length) setPlatforms(tpl.default_platforms);
    if (tpl.default_verticals?.length) setVerticals(tpl.default_verticals);
    if (tpl.default_num_creatives) setNumCreatives(String(tpl.default_num_creatives));
    if (tpl.default_suggested_run_qty) setSuggestedRunQty(String(tpl.default_suggested_run_qty));
    if (tpl.default_concept_notes) setConceptNotes(tpl.default_concept_notes);
    if (tpl.default_notes_to_creative) setNotesToCreative(tpl.default_notes_to_creative);
    if (tpl.default_notes_to_buyer) setNotesToBuyer(tpl.default_notes_to_buyer);
  }, [selectedTemplateId, templates]);

  // Resolved type: use custom text when "Other" is selected
  const resolvedRequestType = requestType === OTHER_REQUEST_TYPE
    ? (customRequestType.trim() || OTHER_REQUEST_TYPE)
    : requestType;

  // ── validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (!requestType) return 'Request type is required';
    if (requestType === OTHER_REQUEST_TYPE && !customRequestType.trim()) return 'Please describe the request type';
    if (platforms.length === 0) return 'At least one platform is required';
    if (verticals.length === 0) return 'At least one vertical is required';
    if (!numCreatives || Number(numCreatives) < 1) return 'Number of creatives must be at least 1';
    return null;
  };

  // ── submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (asDraft = false) => {
    setError('');
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setCreating(true);
    try {
      const payload: any = {
        request_type: resolvedRequestType,
        platforms,
        verticals,
        concept_notes: conceptNotes,
        num_creatives: Number(numCreatives),
        suggested_run_qty: suggestedRunQty ? Number(suggestedRunQty) : undefined,
        delivery_deadline: deliveryDeadline || undefined,
        test_deadline: testDeadline || undefined,
        notes_to_creative: notesToCreative || undefined,
        notes_to_buyer: notesToBuyer || undefined,
        creative_head_id: creativeHeadId || undefined,
        buyer_head_id: buyerHeadId || undefined,
        editor_ids: selectedEditorIds.length > 0 ? selectedEditorIds : undefined,
        buyer_ids: selectedBuyerIds.length > 0 ? selectedBuyerIds : undefined,
        status: asDraft ? 'draft' : 'in_production',
        save_as_template: saveAsTemplate,
        template_name: saveAsTemplate ? templateName : undefined,
      };

      const response = await launchRequestApi.create(payload);
      const newRequest = response.data.data;
      setCreatedRequestId(newRequest.id);

      // Save canvas brief if content was provided
      if (canvasBriefHeadline || canvasBriefScript || canvasBriefFiles.length > 0) {
        try {
          const briefContent = JSON.stringify({
            headline: canvasBriefHeadline,
            script: canvasBriefScript,
            samples: canvasBriefFiles.map(f => ({
              filename: f.name,
              instruction: '',
              type: f.type,
              size: f.size
            })),
            created_at: new Date().toISOString()
          });
          await launchRequestApi.canvas.upsert(newRequest.id, briefContent);
          // Upload each attachment file
          for (const file of canvasBriefFiles) {
            await launchRequestApi.canvas.uploadAttachment(newRequest.id, file);
          }
        } catch (canvasErr) {
          console.error('Failed to save canvas brief:', canvasErr);
          // Show warning but still mark as success since the request was created
          setError('Launch request created, but canvas brief failed to save. You can add it later from the request details.');
        }
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create launch request');
    } finally {
      setCreating(false);
    }
  };



  // ── canvas open after create ──────────────────────────────────────────────
  if (showCanvas && createdRequestId) {
    return (
      <CanvasEditor
        requestId={createdRequestId}
        canvasApiOverride={launchRequestApi.canvas}
        onClose={() => {
          setShowCanvas(false);
          onSuccess();
          onClose();
        }}
      />
    );
  }

  // ── success screen ────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-background rounded-xl shadow-xl w-full max-w-md p-8 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <FileText className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold">Launch Request Created!</h2>
          <p className="text-muted-foreground text-sm">
            Your launch request has been submitted and will be reviewed by the creative and buyer heads.
          </p>
          <div className="flex gap-3 justify-center pt-2">
            <Button variant="outline" onClick={() => { onSuccess(); onClose(); }}>
              Done
            </Button>
            <Button
              onClick={() => setShowCanvas(true)}
            >
              <FileText className="w-4 h-4 mr-2" />
              Open Canvas Brief
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── main form ─────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b shrink-0">
          <div>
            <h2 className="text-lg font-semibold">New Launch Request</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Suggest creatives for media buyers to test</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Template picker */}
          {templates.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-1.5">Use Template</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedTemplateId}
                onChange={e => setSelectedTemplateId(e.target.value)}
              >
                <option value="">— No template —</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Request Type */}
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Request Type <span className="text-destructive">*</span>
            </label>
            <SearchableSelect
              value={requestType}
              onChange={setRequestType}
              placeholder="Select type..."
              options={[
                { value: '', label: 'Select type...' },
                ...FILE_REQUEST_TYPES.map(t => ({ value: t, label: t })),
                { value: OTHER_REQUEST_TYPE, label: 'Other (specify below)' }
              ]}
            />
            {requestType === OTHER_REQUEST_TYPE && (
              <input
                type="text"
                value={customRequestType}
                onChange={e => setCustomRequestType(e.target.value)}
                placeholder="Describe the request type..."
                className="mt-2 w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
              />
            )}
          </div>

          {/* Platforms */}
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Platform(s) <span className="text-destructive">*</span>
            </label>
            <MultiSelect
              options={PLATFORMS.map(p => ({ id: p, label: p }))}
              selectedIds={platforms}
              onChange={setPlatforms}
              placeholder="Select platforms..."
            />
          </div>

          {/* Verticals */}
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Vertical(s) <span className="text-destructive">*</span>
            </label>
            <MultiSelect
              options={VERTICALS.map(v => ({ id: v, label: v }))}
              selectedIds={verticals}
              onChange={setVerticals}
              placeholder="Select verticals..."
            />
          </div>

          {/* Concept Notes */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Concept Notes / Brief</label>
            <textarea
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
              rows={3}
              placeholder="Describe the creative concept, angles, references..."
              value={conceptNotes}
              onChange={e => setConceptNotes(e.target.value)}
            />
          </div>

          {/* Canvas Brief Section */}
          <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/10 space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <label className="text-sm font-medium">Canvas Brief</label>
              <span className="text-xs text-muted-foreground">(optional)</span>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Headline</label>
              <Input
                placeholder="Enter the main headline..."
                value={canvasBriefHeadline}
                onChange={e => setCanvasBriefHeadline(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Script</label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                rows={3}
                placeholder="Write the script or detailed description..."
                value={canvasBriefScript}
                onChange={e => setCanvasBriefScript(e.target.value)}
              />
            </div>

            {/* Reference file uploads with drag-and-drop */}
            <div>
              <label className="block text-xs font-medium mb-1">Reference Samples</label>
              <div
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); setIsBriefDragging(true); }}
                onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setIsBriefDragging(false); }}
                onDrop={handleBriefDrop}
                className={`relative p-3 border-2 border-dashed rounded-lg transition-colors ${
                  isBriefDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                {isBriefDragging && (
                  <div className="absolute inset-0 flex items-center justify-center bg-blue-50/80 dark:bg-blue-900/30 rounded-lg pointer-events-none z-10">
                    <div className="text-center">
                      <Upload className="w-8 h-8 mx-auto mb-1 text-blue-500" />
                      <p className="text-sm font-medium text-blue-600">Drop files or folders here</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 cursor-pointer transition-colors">
                    <Upload className="w-3.5 h-3.5" />
                    Add Files
                    <input
                      type="file"
                      multiple
                      onChange={e => {
                        const files = Array.from(e.target.files || []);
                        setCanvasBriefFiles(prev => [...prev, ...files]);
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 cursor-pointer transition-colors">
                    <FolderOpen className="w-3.5 h-3.5" />
                    Add Folder
                    <input
                      type="file"
                      // @ts-ignore
                      webkitdirectory="true"
                      directory="true"
                      multiple
                      onChange={e => {
                        const files = Array.from(e.target.files || []);
                        setCanvasBriefFiles(prev => [...prev, ...files]);
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                  {canvasBriefFiles.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {canvasBriefFiles.length} file(s) selected
                    </span>
                  )}
                </div>
                {canvasBriefFiles.length === 0 && !isBriefDragging && (
                  <p className="text-xs text-center text-muted-foreground mt-2">
                    Or drag and drop files / folders here
                  </p>
                )}
              </div>
              {canvasBriefFiles.length > 0 && (
                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {canvasBriefFiles.map((file, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-200 dark:border-gray-700">
                      <span className="truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setCanvasBriefFiles(prev => prev.filter((_, idx) => idx !== i))}
                        className="ml-2 text-red-500 hover:text-red-700 shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Creatives to produce + Suggested run qty */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">
                No. of Creatives <span className="text-destructive">*</span>
              </label>
              <Input
                type="number"
                min={1}
                placeholder="e.g. 10"
                value={numCreatives}
                onChange={e => setNumCreatives(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Suggested Run Qty</label>
              <Input
                type="number"
                min={1}
                placeholder="e.g. 5"
                value={suggestedRunQty}
                onChange={e => setSuggestedRunQty(e.target.value)}
              />
            </div>
          </div>

          {/* Deadlines */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Delivery Deadline</label>
              <Input
                type="datetime-local"
                value={deliveryDeadline}
                onChange={e => setDeliveryDeadline(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Test Deadline (Buyer)</label>
              <Input
                type="datetime-local"
                value={testDeadline}
                onChange={e => setTestDeadline(e.target.value)}
              />
            </div>
          </div>

          {/* Assign Heads */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Assign Vertical Head</label>
              <SearchableSelect
                value={creativeHeadId}
                onChange={setCreativeHeadId}
                placeholder="— Select vertical head —"
                options={[
                  { value: '', label: '— Select vertical head —' },
                  ...verticalHeads.map(u => ({ value: u.id, label: u.name }))
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Assign Buyer Head</label>
              <SearchableSelect
                value={buyerHeadId}
                onChange={setBuyerHeadId}
                placeholder="— Select buyer head —"
                options={[
                  { value: '', label: '— Select buyer head —' },
                  ...buyers.map(b => ({ value: b.id, label: b.name }))
                ]}
              />
            </div>
          </div>

          {/* Advanced section toggle */}
          <button
            type="button"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full py-1"
            onClick={() => setShowAdvanced(v => !v)}
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showAdvanced ? 'Hide' : 'Show'} advanced options
          </button>

          {showAdvanced && (
            <div className="space-y-5 border-t pt-4">

              {/* Assign Editors - hidden when editor creates (they are auto-assigned) */}
              {!isEditorCreator && (
                <div>
                  <label className="block text-sm font-medium mb-1.5">Assign Editors (Creative Side)</label>
                  <MultiSelect
                    options={editors.map(e => ({ id: e.id, label: e.name }))}
                    selectedIds={selectedEditorIds}
                    onChange={setSelectedEditorIds}
                    placeholder="Select editors..."
                  />
                </div>
              )}
              {isEditorCreator && (
                <p className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  You will be automatically assigned as the editor for this request.
                  You can assign it to a team lead or buyer below.
                </p>
              )}

              {/* Assign Media Buyers */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Assign Media Buyers</label>
                <MultiSelect
                  options={buyers.map(b => ({ id: b.id, label: `${b.name} (${b.email})` }))}
                  selectedIds={selectedBuyerIds}
                  onChange={setSelectedBuyerIds}
                  placeholder="Select buyers..."
                />
              </div>

              {/* Notes to Creative */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Notes to Vertical Head</label>
                <textarea
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                  rows={2}
                  placeholder="Internal notes for the creative team..."
                  value={notesToCreative}
                  onChange={e => setNotesToCreative(e.target.value)}
                />
              </div>

              {/* Notes to Buyer */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Notes to Buyer Head</label>
                <textarea
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                  rows={2}
                  placeholder="Internal notes for the buyer team..."
                  value={notesToBuyer}
                  onChange={e => setNotesToBuyer(e.target.value)}
                />
              </div>

              {/* Canvas brief: Open full editor after creation */}
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <input
                  type="checkbox"
                  id="create-canvas"
                  checked={showCanvas}
                  onChange={e => setShowCanvas(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="create-canvas" className="text-sm">
                  Open full Canvas Editor after creating (for rich editing)
                </label>
              </div>
            </div>
          )}

          {/* Save as template */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="save-template"
                checked={saveAsTemplate}
                onChange={e => setSaveAsTemplate(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="save-template" className="text-sm flex items-center gap-1.5">
                <Save className="w-3.5 h-3.5" />
                Save as template
              </label>
            </div>
            {saveAsTemplate && (
              <Input
                placeholder="Template name..."
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
              />
            )}
          </div>

          {error && (
            <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-lg">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t shrink-0">
          <Button variant="outline" onClick={() => handleSubmit(true)} disabled={creating}>
            Save as Draft
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={() => handleSubmit(false)} disabled={creating}>
              {creating ? 'Creating...' : 'Create & Send for Production'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
